import styled, { css } from 'styled-components';
import { media } from '../../styles/theme';

export const Container = styled.div`
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;

  ${media.phone} {
    padding: 16px 12px;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  ${media.phone} {
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }
`;

export const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;

  ${media.phone} {
    font-size: 22px;
  }
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

export const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: ${props => props.color || '#F1AF59'};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  /* 手机上放大触摸目标 ≥44px */
  ${media.phone} {
    min-height: 44px;
    padding: 11px 18px;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  ${media.phone} {
    flex-wrap: wrap;
  }
`;

export const SmallButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: ${props => props.color || '#F1AF59'};
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  ${media.phone} {
    min-height: 40px;
    padding: 9px 14px;
  }
`;

export const FiltersBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;

  /* 手机上筛选条竖向堆叠，避免横向溢出 */
  ${media.phone} {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 16px;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #F1AF59;
  }

  ${media.phone} {
    min-height: 44px;
  }
`;

export const FilterSelect = styled.select`
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #F1AF59;
  }

  ${media.phone} {
    min-height: 44px;
  }
`;

export const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  /* 默认：手机上保持列宽，外层 TableContainer 横向滚动，而不是把列挤扁 */
  ${media.phone} {
    min-width: 640px;
  }

  /* 卡片模式（opt-in：给 <Table $cards>）：手机上每行变成一张卡片，字段竖排 label+value，
     比横向滚动更像 App。需在各 <Td> 上加 data-label="字段名"。 */
  ${props => props.$cards && css`
    ${media.phone} {
      min-width: 0;
      display: block;

      thead { display: none; }
      tbody { display: block; }

      tr {
        display: block;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        margin-bottom: 12px;
        padding: 4px 14px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
      }

      td {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 0;
        text-align: right;
        border-bottom: 1px solid #f3f4f6;
        min-width: 0;
      }

      td:last-child { border-bottom: none; }

      td::before {
        content: attr(data-label);
        flex: 0 0 38%;
        text-align: left;
        font-size: 12px;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
      }
    }
  `}
`;

export const Th = styled.th`
  padding: 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;

  ${media.phone} {
    padding: 12px 10px;
  }
`;

export const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #f3f4f6;

  ${media.phone} {
    padding: 12px 10px;
  }
`;

export const UserName = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.color};
  color: white;
  text-transform: capitalize;
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: ${props => props.color};
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  ${media.phone} {
    min-height: 40px;
    padding: 8px 12px;
    font-size: 13px;
  }
`;

export const Stats = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin: 4px 0;
`;

export const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: ${props => props.width > 80 ? '#10b981' : props.width > 50 ? '#f59e0b' : '#ef4444'};
  transition: width 0.3s;
`;

export const ProgressText = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

// Course Groups Specific Styles
export const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 24px;
  height: calc(100vh - 200px);

  /* 平板及以下：固定 400px 侧栏会溢出，改为单列、高度自适应（配合页面做列表/详情切换） */
  ${media.tablet} {
    grid-template-columns: 1fr;
    height: auto;
    gap: 16px;
  }
`;

export const GroupList = styled.div`
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
`;

export const GroupCard = styled.div`
  background: white;
  border: 2px solid ${props => props.$isSelected ? '#F1AF59' : '#e5e7eb'};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #F1AF59;
    box-shadow: 0 2px 8px rgba(241, 175, 89, 0.15);
  }
`;

export const GroupCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
`;

export const GroupName = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

export const GroupInfo = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;

  div {
    margin-bottom: 4px;
  }
`;

export const DetailPanel = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
`;

export const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
`;

export const Card = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  overflow: hidden;
`;

export const CardHeader = styled.div`
  padding: 16px 20px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  padding: 20px;

  ${media.phone} {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 16px;
  }
`;

export const InfoItem = styled.div``;

export const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

export const InfoValue = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
`;

export const StudentList = styled.div`
  padding: 12px 20px;
`;

export const StudentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

export const StudentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
`;

export const StudentDetail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

export const FeedbackList = styled.div`
  padding: 12px 20px;
`;

export const FeedbackItem = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

export const FeedbackHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 8px;
`;

export const FeedbackTeacher = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;

export const FeedbackDate = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

export const FeedbackContent = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  font-size: 14px;
`;

// Materials specific
export const MaterialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

export const MaterialCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

export const MaterialIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.color || '#F1AF59'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: 16px;
`;

export const MaterialName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  word-break: break-word;
`;

export const MaterialMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

export const MaterialStats = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
`;

export const StatItem = styled.div`
  font-size: 12px;
  color: #6b7280;

  strong {
    color: #1f2937;
    font-weight: 600;
  }
`;

// Stats components for dashboards
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
`;

export const StatCard = styled.div`
  background: ${props => props.color};
  color: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

export const StatNumber = styled.div`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 8px;
`;

export const StatLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  opacity: 0.9;
  margin-bottom: 4px;
`;

export const StatSubtext = styled.div`
  font-size: 13px;
  opacity: 0.8;
`;

// Modal Components
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  /* 手机上近全屏，底部按钮（保存/取消）始终可达 */
  ${media.phone} {
    width: 100%;
    max-width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #1f2937;
  }
`;

export const ModalContent = styled.div`
  padding: 24px;
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

export const FormGroup = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #F1AF59;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.15);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  font-family: inherit;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: #F1AF59;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.15);
  }
`;

export const Note = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 12px;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 6px;
  line-height: 1.5;
`;

export const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
`;

export const RoleBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const RoleSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const RoleOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 2px solid ${props => props.$selected ? '#F1AF59' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#fef3e2' : 'white'};

  &:hover {
    border-color: #F1AF59;
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
  }
`;

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

// View Toggle for Folder View
export const ViewToggleGroup = styled.div`
  display: flex;
  gap: 4px;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;
`;

export const ViewToggleButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#1f2937' : '#6b7280'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};

  &:hover {
    color: #1f2937;
  }
`;

// Breadcrumb Navigation
export const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  font-size: 14px;
  flex-wrap: wrap;
`;

export const BreadcrumbItem = styled.span`
  color: ${props => props.$active ? '#1f2937' : '#3b82f6'};
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: ${props => props.$active ? 'default' : 'pointer'};

  &:hover {
    text-decoration: ${props => props.$active ? 'none' : 'underline'};
  }
`;

export const BreadcrumbSeparator = styled.span`
  color: #9ca3af;
`;

// Folder Grid
export const FolderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
`;

// Folder Card
export const FolderCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &:hover {
    border-color: #F1AF59;
    box-shadow: 0 4px 12px rgba(241, 175, 89, 0.15);
    transform: translateY(-2px);
  }
`;

export const FolderIcon = styled.div`
  width: 64px;
  height: 52px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border-radius: 4px 4px 8px 8px;
  position: relative;
  margin-bottom: 12px;

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 0;
    width: 28px;
    height: 12px;
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    border-radius: 4px 4px 0 0;
  }
`;

export const FolderLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

export const FolderCount = styled.div`
  font-size: 12px;
  color: #6b7280;
`;
